/**
 * Client-side SDMS mail-merge — the port of legacy ALPACA's DevExpress
 * `GenerateMailMerge` (BTLIMS SpreadsheetEntryController.cs:3398).
 *
 * Runs in the browser with **ExcelJS** (the same engine Univer uses to read these
 * DevExpress-authored templates) so the merged workbook stays ExcelJS-readable — a
 * server-side ClosedXML merge emits namespace-prefixed OOXML that ExcelJS's strict
 * parser rejects (verified 2026-07-28). The backend supplies the raw template + the
 * expanded records (`AI_Analysis_Sdms_MergeData_SP`); this does the substitution.
 *
 * The template stores merge fields as `FIELD("COLUMN")` formulas. Rows above the
 * detail band carry single header FIELDs (TEST/METHOD/…); the band row (the one with
 * the most FIELD cells — the template's `DETAILRANGE`) is repeated once per record.
 * v1 expands ONLY the main worksheet (index 0 = OG's RawDataTableDataSource); the
 * Summary / ISTD / Reagents sub-sheets get single substitution (their own datasets
 * are a documented follow-up). Result columns (Reading / NumericResult / Qualifier /
 * QCStatus) stay blank — the analyst enters them.
 */
type Row = Record<string, unknown>;

const FIELD_RE = /FIELD\("([^"]*)"\)/gi;
const PURE_RE  = /^\s*FIELD\("([^"]*)"\)\s*$/i;
// Some template cells carry merge fields as a TEXT placeholder `[[COLUMN]]`
// instead of a `FIELD("COLUMN")` formula (e.g. the CtrlLimit columns). These live
// in plain-string cells, so the FIELD/formula path above never sees them — they
// must be substituted separately or they render literally (`[[%RPDULIMIT]]`) and a
// dependent formula errors `#VALUE!`. (fix 2026-07-30)
const BRACKET_RE      = /\[\[([^\]]+)\]\]/g;          // global — for replace
const BRACKET_PURE_RE = /^\s*\[\[([^\]]+)\]\]\s*$/;   // whole cell is one [[FIELD]]
const HAS_FIELD       = /FIELD\(/i;                   // non-global — safe for .test()
const HAS_BRACKET     = /\[\[[^\]]+\]\]/;

/** FIELD names that are LEGITIMATELY absent from the merge dataset — the analyst
 *  (or an instrument import) supplies them AFTER the merge, so the template cell
 *  must start blank. Everything else that a template references but the dataset
 *  does not carry is template↔SP drift — a data-accuracy bug in the making (the
 *  ISTD Acceptance Range shipped blank for exactly this reason) — and gets a
 *  console warning below so it can never fail silently again. (2026-08-15) */
const ENTRY_FIELDS = new Set([
  'READING', 'READING1', 'READING2', 'NUMERICRESULT', 'QUALIFIER', 'QCSTATUS',
  'MASS', 'ABSORBANCE', 'INSTRUMENTRESULT', 'QCSAMPLERESULT', 'SHEETNAME',
  // Result-derived at entry time (OG's DataTable carries them EMPTY at create):
  'RPD', '%RECOVERY', 'DUPSOURCERESULT', 'RESPONSE',
  // Bound by 15 templates but supplied by NOTHING in OG either (verified: the
  // string exists nowhere in the OG source) — blank in OG too, so not drift:
  'ISREPORTDONE',
]);
/** USERDEFINED1..n / USERDEFINEDDATE1.. (pH temp/dilutions, PFAS responses/
 *  descriptions) are instrument-import/entry slots — legitimately absent at
 *  merge time. */
const ENTRY_FIELD_RE = /^USERDEFINED/;

/** Per-sheet right edge (1-based column) of the template's DETAILRANGE defined
 *  names, parsed from the raw xlsx zip (`xl/workbook.xml`). The sheet each
 *  range belongs to is taken from the range's own `Sheet!$A$1:$B$2` prefix, so
 *  sheet-local duplicates (Sheet1 + Reagents both define DETAILRANGE) resolve
 *  correctly where ExcelJS's definedNames model cannot. */
async function detailRangeEndCols(templateBuf: ArrayBuffer): Promise<Record<string, number>> {
  const out: Record<string, number> = {};
  const JSZip = (await import('jszip')).default;
  const zip = await JSZip.loadAsync(templateBuf);
  const xml = await zip.file('xl/workbook.xml')?.async('string');
  if (!xml) return out;
  for (const dn of xml.matchAll(/<definedName\b[^>]*>([^<]*)<\/definedName>/g)) {
    if (!/name="DETAILRANGE"/i.test(dn[0]!)) continue;
    const m = /^'?([^'!]+)'?!\$?[A-Za-z]{1,3}\$?\d+:\$?([A-Za-z]{1,3})\$?\d+$/.exec(dn[1]!.trim());
    if (m) out[m[1]!] = colToNum(m[2]!);
  }
  return out;
}
function colToNum(letters: string): number {
  let n = 0;
  for (const ch of letters.toUpperCase()) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n;
}

/** Shift the RELATIVE row part of every A1 cell-reference in a formula by `k` — the
 *  standard Excel copy semantics ClosedXML would apply automatically. Absolute rows
 *  (`$8`) and refs that are part of a longer token (function names) are left alone. */
function shiftFormula(f: string, k: number): string {
  if (!k) return f;
  return f.replace(/(\$?)\b([A-Z]{1,3})(\$?)(\d+)\b/g, (m, colDollar, col, rowDollar, row) =>
    rowDollar === '$' ? m : `${colDollar}${col}${rowDollar}${parseInt(row, 10) + k}`);
}

/** Excel-literal for a value embedded inside a formula — ALWAYS quoted TEXT.
 *
 *  DevExpress `FIELD()` returns TEXT, and the templates are authored for that:
 *  they compare fields to text (`FIELD("INTERNALSTANDARD")="1"`) and explicitly
 *  `VALUE(...)`-wrap a field before arithmetic. Splicing a NUMBER unquoted broke
 *  the text comparisons — the API sends InternalStandard as the NUMBER 1, so the
 *  ISTD Acceptance Range became `IF(1="1",…)` which is FALSE in Excel semantics
 *  (number ≠ text) and the column rendered blank while the sibling cells that
 *  reference the AU helper CELL (stored as text) worked. Quoting numbers is safe
 *  for arithmetic too: Univer coerces numeric text exactly like Excel (verified
 *  2026-08-13, see cellValue note). (fix 2026-08-14) */
function fieldLiteral(v: unknown): string {
  if (v === undefined || v === null || v === '') return '""';
  return `"${String(v).replace(/"/g, '""')}"`;
}

/** Value written into a pure-FIELD cell.
 *
 *  ⚠️ A blank field yields **null** (a TRULY empty cell), never `''`.  Excel/Univer
 *  treat a cell containing an empty STRING as NON-blank, so writing `''` made the
 *  templates' own guards misfire: `QCStatus` computed Fail/Pass on a freshly loaded
 *  sheet (OG leaves it blank until results are entered) and `RPD CtrlLimit` rendered
 *  `0` instead of blank, because `ISBLANK(...)` / `...=""` never matched and the
 *  arithmetic ran on "". (fix 2026-08-13, user OG side-by-side)
 *
 *  NOTE (2026-08-13): numeric fields arrive from the merge SP as TEXT
 *  ("1413", "0.566") and are written as text here. That was investigated as the
 *  cause of blank Rec %/RPD % and RULED OUT — Univer's engine coerces numeric
 *  text in arithmetic just as Excel does (verified directly:
 *  `NumberValueObject(100).divided(StringValueObject('1413'))` → 0.0707, no
 *  error). So do not "fix" this by coercing to numbers; it changes cell type and
 *  formatting for no behavioural gain. */
function cellValue(v: unknown): string | number | null {
  if (v === undefined || v === null) return null;          // truly empty, NOT ''
  if (typeof v === 'number') return v;
  const s = String(v);
  return s.trim() === '' ? null : s;                       // '' / whitespace → empty cell
}

/** Write a resolved FIELD value into a cell, matching DevExpress `FIELD()` display.
 *
 *  DevExpress `FIELD()` returns TEXT, so a numeric field value is shown VERBATIM
 *  (general) — the cell's number PATTERN does NOT reformat it. The templates put
 *  "0.0" on reference columns (DF, Spike Amount, the limits), and Univer applies
 *  that pattern to the value, so a whole number gained a trailing decimal: Spike
 *  Amount "1413" → "1413.0", DF "1" → "1.0", where OG shows "1413" / "1". Neutralise
 *  the pattern to General for a cell that actually receives a numeric value; a blank
 *  field keeps its pattern so a number the analyst later types still formats.
 *  (fix 2026-08-13, user OG side-by-side) */
function setFieldCell(cell: { value: unknown; numFmt?: string }, value: unknown): void {
  const cv = cellValue(value);
  cell.value = cv;
  if (cv !== null && Number.isFinite(Number(cv))) cell.numFmt = 'General';
}

/** Normalize a merge-field / column key for comparison: uppercase and strip ONE
 *  pair of surrounding square brackets.  The Alkalinity-style templates bind the
 *  control-limit helper cells via `FIELD("[%RPDULIMIT]")` — the field name itself
 *  carries brackets — while the SP emits the column as `%RPDULIMIT` (SQL strips the
 *  `[...]` alias delimiter).  Without this the four Rec/RPD limit fields never bind,
 *  so the RPD/Rec CtrlLimit columns render `[[%RPDULIMIT]]` / `#VALUE!`. (fix 2026-08-05) */
function normKey(k: string): string {
  return k.toUpperCase().replace(/^\[(.+)\]$/, '$1');
}

/** Case-insensitive, bracket-insensitive lookup: record wins, then the batch header, then blank.
 *  A name with NO matching column at all (≠ a present-but-null value) is recorded in
 *  `missing` — after the merge those are console-warned as template↔data drift. */
function lookup(key: string, rec: Row, headerUpper: Row, missing?: Set<string>): unknown {
  const K = normKey(key);
  for (const kk of Object.keys(rec)) if (normKey(kk) === K) return rec[kk];
  for (const hk of Object.keys(headerUpper)) if (normKey(hk) === K) return headerUpper[hk];
  if (missing && !ENTRY_FIELDS.has(K) && !ENTRY_FIELD_RE.test(K)) missing.add(key);
  return undefined;
}

/** Merge the template with the batch records → the merged .xlsx as an ArrayBuffer. */
export async function mergeSdmsTemplate(
  templateBuf: ArrayBuffer,
  header: Row,
  records: Row[],
  /** OUT — where each header FIELD landed, as `Sheet!A1`, keyed by the UPPERCASE
   *  field name. A header value that is not known until AFTER the merge (QCBATCHID:
   *  the id is minted by the save) can then be stamped into its cell instead of
   *  leaving the blank the merge wrote (client 2026-08-19). */
  fieldRefs?: Record<string, string>,
): Promise<ArrayBuffer> {
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(templateBuf);

  const headerUpper: Row = {};
  for (const k of Object.keys(header)) headerUpper[k.toUpperCase()] = header[k];
  // Template↔data drift detector: FIELD names the template references that NO
  // record/header column supplies (see lookup). Warned once, deduped, after the
  // merge — a silent blank column is a data-accuracy bug, not a cosmetic one.
  const missing = new Set<string>();

  const ws = wb.worksheets[0];
  if (ws) {
    // Detail band = the row with the most FIELD cells (the template's DETAILRANGE).
    let band = -1, best = 0;
    for (let r = 1; r <= Math.min(ws.rowCount, 60); r++) {
      let n = 0;
      ws.getRow(r).eachCell({ includeEmpty: false }, c => {
        if (typeof c.formula === 'string' && /FIELD\(/i.test(c.formula)) n++;
      });
      if (n > best) { best = n; band = r; }
    }

    if (band > 0 && best >= 5) {
      const n = Math.max(1, records.length);
      if (n > 1) ws.duplicateRow(band, n - 1, true);   // copy styles + formulas into the new rows
      for (let i = 0; i < n; i++) {
        const rec = i < records.length ? records[i]! : {};
        substituteRow(ws.getRow(band + i), rec, headerUpper, i, missing, /* dropStaleCache */ true, fieldRefs, ws.name);
        // NOTE (2026-08-12): `resolveDisplayFormulas` used to run here, pre-computing
        // TWO hard-coded formula shapes (a pure cell ref, and VALUE(a)&"sep"&VALUE(b))
        // into literals — a workaround for the era when Univer received no formulas and
        // rendered stale cached results.  Univer now evaluates formulas live
        // (SdmsUniverSheet emits `f` for every formula cell), so that special-casing is
        // both unnecessary and WRONG: it only ever covered this template's two shapes,
        // and turning a formula into a literal also stopped it recalculating and left
        // the cell editable where OG locks it.  Removed so EVERY formula, in every
        // template, is computed by the one generic engine path.
      }
      // Header FIELD rows (above the band) — single substitution, no row shift.
      for (let r = 1; r < band; r++) substituteRow(ws.getRow(r), {}, headerUpper, 0, missing, false, fieldRefs, ws.name);

      // ── Trim everything BELOW the expanded detail band ────────────────────
      // The templates carry author notes just under the detail row — 9116 has
      // "Result needs to be configuered with correct number of Sig Figs…",
      // "%R,%RPD,Qualifier need to be configured", "We need a way to have more than
      // one template per method" at rows 8-10, plus "From Test Code …" labels off to
      // the right. Expanding the band pushes them down but they stay visible, and OG
      // does not show them (its DevExpress merge emits only the header + detail
      // range). So the merged sheet keeps rows 1..lastDataRow and drops the rest.
      //
      // This edits the IN-MEMORY workbook only — `wb` was loaded from the template
      // BUFFER and written back out as a new buffer, so the stored template blob in
      // SpreadSheetBuilder_TemplateInfo is untouched and OG keeps seeing its notes.
      //
      // Cleared via `row.values = []`, NOT `ws.spliceRows(...)`: ExcelJS's spliceRows
      // is a silent NO-OP when the removed rows run to the end of the sheet (measured
      // — content and rowCount both unchanged), so it looked right and did nothing.
      // Emptying each row's values genuinely drops them: rowCount falls to the last
      // data row, so the used range ends at the data and nothing below can catch a
      // conditional-format range.
      const lastDataRow = band + n - 1;
      for (let r = ws.rowCount; r > lastDataRow; r--) ws.getRow(r).values = [];
    } else {
      // No band detected — substitute every FIELD once so none render as #NAME?.
      for (let r = 1; r <= ws.rowCount; r++) substituteRow(ws.getRow(r), {}, headerUpper, 0, missing, false, fieldRefs, ws.name);
    }
  }

  // Remaining sheets (Summary / ISTD / Reagents …): single substitution (v1).
  for (let s = 1; s < wb.worksheets.length; s++) {
    const sheet = wb.worksheets[s]!;
    for (let r = 1; r <= sheet.rowCount; r++) substituteRow(sheet.getRow(r), {}, headerUpper, 0, missing, false, fieldRefs, sheet.name);
  }

  if (missing.size) {
    console.warn(
      '[SDMS merge] template FIELD name(s) with NO matching data column — these cells rendered BLANK. '
      + 'Either the merge SP must emit the column or the name is a template typo: '
      + [...missing].sort().join(', '));
  }

  // ── Hide the columns OG never shows: right of each sheet's DETAILRANGE ─────
  // OG's DevExpress merge copies ONLY the HEADERRANGE/DETAILRANGE named ranges
  // into its output workbook, so a per-template config block sitting to the
  // right of the band (pH 1027 keeps a SigFig/CutOff/Decimal table at AF..AN)
  // never appears in OG at all. Mark those columns hidden so every downstream
  // view treats them exactly like the template's own hidden helper columns.
  // Read from the RAW template zip: ExcelJS collapses same-named sheet-local
  // defined names on load (only one sheet's DETAILRANGE survives its model), so
  // the parsed workbook can't answer this. Templates without the name (Metals
  // 8070) are untouched. (user pH side-by-side 2026-08-17)
  try {
    const ends = await detailRangeEndCols(templateBuf);
    for (const sheet of wb.worksheets) {
      const end = ends[sheet.name];
      if (end) for (let c = end + 1; c <= sheet.columnCount; c++) sheet.getColumn(c).hidden = true;
    }
  } catch { /* malformed defined names — leave the columns as authored */ }

  return (await wb.xlsx.writeBuffer()) as ArrayBuffer;
}

/** Substitute the FIELD() cells in one row: pure FIELD → literal value; embedded
 *  FIELD (inside IF/IFERROR/…) → the value spliced into the formula, whose relative
 *  refs are shifted by `k` for the k-th repeated band row. */
function substituteRow(row: ReturnType<import('exceljs').Worksheet['getRow']>, rec: Row, headerUpper: Row, k: number, missing?: Set<string>, dropStaleCache = false, fieldRefs?: Record<string, string>, sheetName?: string): void {
  row.eachCell({ includeEmpty: false }, cell => {
    const f = cell.formula;
    if (typeof f === 'string') {
      // Formula cell — may embed FIELD("…") and/or [[…]] merge fields.
      if (HAS_FIELD.test(f) || HAS_BRACKET.test(f)) {
        const pure = f.match(PURE_RE);
        if (pure) {
          setFieldCell(cell, lookup(pure[1]!, rec, headerUpper, missing));
          // Remember WHERE this field landed so a value that only exists after the
          // merge can still be written into the right cell.
          if (fieldRefs && sheetName) {
            const key = pure[1]!.toUpperCase();
            if (!fieldRefs[key]) fieldRefs[key] = `${sheetName}!${cell.address}`;
          }
        } else {
          let sub = f.replace(FIELD_RE, (_m, name) => fieldLiteral(lookup(name, rec, headerUpper, missing)));
          sub = sub.replace(BRACKET_RE, (_m, name) => fieldLiteral(lookup(name, rec, headerUpper, missing)));
          cell.value = { formula: shiftFormula(sub, k) };
        }
      } else if (k || dropStaleCache) {
        // A non-FIELD formula copied into a repeated band row still needs its
        // same-row refs shifted (e.g. AT8 → AT9). Re-setting the cell also DROPS
        // the template's cached result — and `dropStaleCache` forces that on the
        // FIRST band row (k=0) too: DevExpress saves whatever the authoring
        // session last displayed, so the band carries junk caches like
        // '[NUMERICRESULT]', and since the Univer converter now SEEDS cached
        // results as display values (team 2026-08-16), row 10 of a FRESH merge
        // rendered that junk in the Result column while rows 11+ (re-set here)
        // were clean — user pH side-by-side 2026-08-17. A fresh merge has no
        // legitimate caches; saved sheets (real entered values) don't pass
        // through this merge, so their seeds are unaffected. (fix 2026-08-17)
        cell.value = { formula: shiftFormula(f, k) };
      }
      return;
    }
    // Plain TEXT cell carrying a `[[COLUMN]]` placeholder (the CtrlLimit columns):
    // whole-cell → the raw value; embedded → substitute each placeholder inline.
    const v = cell.value;
    if (typeof v === 'string' && HAS_BRACKET.test(v)) {
      const pure = v.match(BRACKET_PURE_RE);
      if (pure) {
        setFieldCell(cell, lookup(pure[1]!, rec, headerUpper, missing));
      } else {
        cell.value = v.replace(BRACKET_RE, (_m, name) => {
          const val = lookup(name, rec, headerUpper, missing);
          return val === undefined || val === null ? '' : String(val);
        });
      }
    }
  });
}
